import React, { useState, useMemo, useEffect } from 'react';
import { X, Trophy, Plus, Trash2 } from 'lucide-react';
import MediaCaptureMenu from '../MediaCaptureMenu';
import PasteUrlInput from '../PasteUrlInput';
import AttachmentTile from '../AttachmentTile';
import ChipInput from './ChipInput';
import JudoTechniqueRow from './JudoTechniqueRow';
import { fileToPayload, urlToPayload } from '../../lib/mediaCapture';
import { useProfile } from '../../context/ProfileContext';
import {
  JUDO_CLASS_FORMATS,
  JUDO_FOCUS_MODES,
  JUDO_FELT_SCALE,
  JUDO_BELTS,
  newJudoTechniqueRow,
  newJudoRandoriStats,
  newJudoReflection,
} from '../../data/combativesJudo';

const DRAFT_KEY = 'enferno_judo_journal_draft_v1';

/**
 * JudoLogDialog — detailed Judo session journal.
 *
 * NOT a BJJ fork. Per JUDO_RESEARCH.md, Judo classifies techniques by
 * mechanism (Te / Ashi / Koshi / Sutemi / Osaekomi / Shime / Kansetsu),
 * not by position-context. This dialog uses JudoClassificationPicker
 * (cascading group → subgroup → technique) instead of PositionPicker,
 * and the sparring section tracks Judo-flavored randori outcomes
 * (throws completed/countered, groundwork submissions) instead of
 * BJJ's positional sparring stats.
 *
 * Schema written: see newJudoSession-equivalent shape at the bottom of
 * commitSave().
 */
const JudoLogDialog = ({ art, initialData = null, onClose, onSave }) => {
  const { profile, training } = useProfile();
  const isEditMode = !!initialData;

  // Belt snapshot — pull from profile.martialArts.judo if present, otherwise blank.
  const profileBelt = profile?.martialArts?.judo?.belt || '';

  // Autocomplete sources — flatten unique values from prior Judo sessions.
  const autocompleteSuggestions = useMemo(() => {
    const techniqueNames = new Set();
    const coachNames = new Set();
    const partnerNames = new Set();
    (training?.sessions || []).forEach((s) => {
      if (s.art_id !== 'judo') return;
      if (initialData && s.id === initialData.id) return;
      (s.techniques || []).forEach((t) => { if (t?.classification?.name) techniqueNames.add(t.classification.name); });
      (s.coaches || []).forEach((c) => { if (c) coachNames.add(c); });
      (s.randori?.partners || []).forEach((p) => { if (p) partnerNames.add(p); });
    });
    return {
      techniques: Array.from(techniqueNames).sort(),
      coaches: Array.from(coachNames).sort(),
      partners: Array.from(partnerNames).sort(),
    };
  }, [training?.sessions, initialData]);

  const todayLocal = useMemo(() => {
    const d = new Date();
    const tzOff = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOff).toISOString().slice(0, 10);
  }, []);
  const minLocal = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    const tzOff = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOff).toISOString().slice(0, 10);
  }, []);

  const buildInitialForm = () => {
    if (!initialData) {
      return {
        date: todayLocal,
        duration: '60',
        classFormat: 'mixed',
        focusModes: [],
        coaches: [],
        beltSnapshot: profileBelt,
        intent: '',
        techniques: [],
        randori: newJudoRandoriStats(),
        reflection: newJudoReflection(),
        notes: '',
        attachments: [],
      };
    }
    const sessionDate = initialData.date ? new Date(initialData.date) : new Date();
    const tzOff = sessionDate.getTimezoneOffset() * 60000;
    const dateStr = new Date(sessionDate.getTime() - tzOff).toISOString().slice(0, 10);
    const numToStr = (n) => (n === 0 || n === undefined || n === null) ? '' : String(n);
    const r = initialData.randori || {};
    return {
      date: dateStr,
      duration: String(initialData.duration ?? '60'),
      classFormat: initialData.classFormat || 'mixed',
      focusModes: initialData.focusModes || [],
      coaches: initialData.coaches || [],
      beltSnapshot: initialData.beltSnapshot || profileBelt,
      intent: initialData.intent || '',
      techniques: (initialData.techniques || []).map((t) => ({
        ...newJudoTechniqueRow(),
        ...t,
      })),
      randori: {
        ...newJudoRandoriStats(),
        ...r,
        rounds: numToStr(r.rounds),
        throwsCompleted: numToStr(r.throwsCompleted),
        throwsCountered: numToStr(r.throwsCountered),
        groundworkSubmissions: numToStr(r.groundworkSubmissions),
        groundworkSubmitted: numToStr(r.groundworkSubmitted),
      },
      reflection: { ...newJudoReflection(), ...(initialData.reflection || {}) },
      notes: initialData.notes || '',
      attachments: initialData.attachments || [],
    };
  };

  const [form, setForm] = useState(buildInitialForm);

  // Draft autosave (create mode only)
  useEffect(() => {
    if (isEditMode) return;
    try {
      const saved = sessionStorage.getItem(DRAFT_KEY);
      if (saved) setForm((f) => ({ ...f, ...JSON.parse(saved) }));
    } catch { /* ignore */ }
  }, [isEditMode]);
  useEffect(() => {
    if (isEditMode) return;
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch { /* ignore */ }
  }, [form, isEditMode]);

  const setRandori = (patch) => setForm((p) => ({ ...p, randori: { ...p.randori, ...patch } }));
  const setReflection = (patch) => setForm((p) => ({ ...p, reflection: { ...p.reflection, ...patch } }));

  // --- Technique CRUD ---
  // prepend new techniques so newest appears on top (standard
  // journal pattern; matches BJJ + Muay Thai dialogs).
  const addTechnique = () => setForm((p) => ({ ...p, techniques: [newJudoTechniqueRow(), ...p.techniques] }));
  const updateTechnique = (idx, next) => setForm((p) => ({
    ...p,
    techniques: p.techniques.map((t, i) => (i === idx ? next : t)),
  }));
  const removeTechnique = (idx) => setForm((p) => ({
    ...p,
    techniques: p.techniques.filter((_, i) => i !== idx),
  }));

  // --- Attachments ---
  const addAttachment = (payload) => setForm((p) => ({ ...p, attachments: [...p.attachments, payload] }));
  const removeAttachment = (id) => setForm((p) => ({ ...p, attachments: p.attachments.filter((a) => a.id !== id) }));
  const addExternalUrl = async (url) => {
    const payload = await urlToPayload(url);
    if (payload) addAttachment(payload);
  };

  // --- Save ---
  const commitSave = () => {
    const sessionDate = new Date(form.date + 'T12:00:00');
    onSave({
      date: sessionDate,
      tz_offset_minutes: new Date().getTimezoneOffset(),
      duration: Number(form.duration) || 60,
      // legacy display field — first focus mode or "Drilling"
      focus: form.focusModes[0] || 'Drilling',
      notes: form.notes,
      attachments: form.attachments,
      // Judo-specific journal payload
      classFormat: form.classFormat,
      focusModes: form.focusModes,
      coaches: form.coaches,
      beltSnapshot: form.beltSnapshot,
      intent: form.intent,
      techniques: form.techniques,
      randori: {
        rounds: Number(form.randori.rounds) || 0,
        throwsCompleted: Number(form.randori.throwsCompleted) || 0,
        throwsCountered: Number(form.randori.throwsCountered) || 0,
        groundworkSubmissions: Number(form.randori.groundworkSubmissions) || 0,
        groundworkSubmitted: Number(form.randori.groundworkSubmitted) || 0,
        feltScale: form.randori.feltScale,
        partners: form.randori.partners,
        notes: form.randori.notes,
      },
      reflection: form.reflection,
    });
    if (!isEditMode) {
      try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 p-0 sm:p-4">
      <div className="w-full max-w-3xl bg-enf-surface-deep border border-enf-surface-border sm:rounded-xl flex flex-col max-h-[100vh] sm:max-h-[90vh]" data-testid="judo-log-dialog">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-enf-surface-border flex-shrink-0">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-enf-accent-fg font-semibold mb-1 flex items-center gap-2">
              <Trophy className="w-3.5 h-3.5" /> {art.shortName} journal {isEditMode && '· Edit'}
            </div>
            <h2 className="font-display text-3xl tracking-wide leading-none">{isEditMode ? 'Edit session' : 'Log a session'}</h2>
          </div>
          <button onClick={onClose} className="text-enf-text-muted hover:text-enf-text-primary p-1" aria-label="Close" data-testid="judo-dialog-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Header row: date / duration / belt */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1 block">Date</label>
              <input
                type="date" value={form.date} min={minLocal} max={todayLocal}
                onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
                data-testid="judo-session-date"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1 block">Duration (min)</label>
              <input
                type="number" value={form.duration} min={5} max={480}
                onChange={(e) => setForm((p) => ({ ...p, duration: e.target.value }))}
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
                data-testid="judo-session-duration"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1 block">Belt</label>
              <select
                value={form.beltSnapshot}
                onChange={(e) => setForm((p) => ({ ...p, beltSnapshot: e.target.value }))}
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
                data-testid="judo-session-belt"
              >
                <option value="">—</option>
                {JUDO_BELTS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>

          {/* Class format + focus modes */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1 block">Class format</label>
              <select
                value={form.classFormat}
                onChange={(e) => setForm((p) => ({ ...p, classFormat: e.target.value }))}
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
                data-testid="judo-class-format"
              >
                {JUDO_CLASS_FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1 block">Focus modes</label>
              <div className="flex flex-wrap gap-1.5">
                {JUDO_FOCUS_MODES.map((mode) => {
                  const active = form.focusModes.includes(mode);
                  return (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setForm((p) => ({
                        ...p,
                        focusModes: active ? p.focusModes.filter((m) => m !== mode) : [...p.focusModes, mode],
                      }))}
                      aria-pressed={active}
                      className={`px-2.5 py-1 text-[11px] rounded border font-semibold transition-colors ${
                        active
                          ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary'
                          : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary'
                      }`}
                      data-testid={`judo-focus-${mode.toLowerCase()}`}
                    >
                      {mode}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Coaches */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1 block">Coaches / Sensei</label>
            <ChipInput
              values={form.coaches}
              onChange={(coaches) => setForm((p) => ({ ...p, coaches }))}
              suggestions={autocompleteSuggestions.coaches}
              placeholder="Add a coach name"
              testId="judo-coaches"
            />
          </div>

          {/* Pre-session intent */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1 block">Pre-session intent</label>
            <textarea
              value={form.intent}
              onChange={(e) => setForm((p) => ({ ...p, intent: e.target.value }))}
              placeholder="What are you working on tonight?"
              rows={2}
              className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-y"
              data-testid="judo-intent"
            />
          </div>

          {/* Techniques */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold">Techniques worked · {form.techniques.length}</label>
              <button
                type="button"
                onClick={addTechnique}
                className="text-xs text-enf-role-info hover:text-enf-text-primary flex items-center gap-1"
                data-testid="judo-add-technique"
              >
                <Plus className="w-3.5 h-3.5" /> Add technique
              </button>
            </div>
            {form.techniques.length === 0 && (
              <div className="text-xs text-enf-text-muted py-3 text-center bg-enf-surface-mid/40 border border-dashed border-enf-surface-border rounded-md">
                No techniques logged. Tap "Add technique" to record what you drilled tonight.
              </div>
            )}
            <div className="space-y-2">
              {form.techniques.map((t, i) => (
                <JudoTechniqueRow
                  key={i}
                  value={t}
                  onChange={(next) => updateTechnique(i, next)}
                  onRemove={() => removeTechnique(i)}
                  index={i}
                  nameSuggestions={autocompleteSuggestions.techniques}
                />
              ))}
            </div>
          </div>

          {/* Randori stats */}
          <div className="p-4 bg-enf-surface-mid border border-enf-surface-border rounded-md space-y-3">
            <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold">Randori (free-practice) stats</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[
                ['rounds', 'Rounds', 'judo-stat-rounds'],
                ['throwsCompleted', 'Throws completed', 'judo-stat-throws-completed'],
                ['throwsCountered', 'Throws received', 'judo-stat-throws-countered'],
                ['groundworkSubmissions', 'GW subs landed', 'judo-stat-gw-subs-landed'],
                ['groundworkSubmitted', 'GW subs given up', 'judo-stat-gw-subs-given'],
              ].map(([key, label, tid]) => (
                <div key={key}>
                  <label className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1 block">{label}</label>
                  <input
                    type="number" min={0} max={99}
                    value={form.randori[key]}
                    onChange={(e) => setRandori({ [key]: e.target.value })}
                    placeholder="0"
                    className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
                    data-testid={tid}
                  />
                </div>
              ))}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1 block">Felt scale (1-5)</label>
                <select
                  value={form.randori.feltScale}
                  onChange={(e) => setRandori({ feltScale: e.target.value })}
                  className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
                  data-testid="judo-stat-felt"
                >
                  <option value="">—</option>
                  {JUDO_FELT_SCALE.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1 block">Partners</label>
              <ChipInput
                values={form.randori.partners}
                onChange={(partners) => setRandori({ partners })}
                suggestions={autocompleteSuggestions.partners}
                placeholder="Add a partner name"
                testId="judo-partners"
              />
            </div>
            <textarea
              value={form.randori.notes}
              onChange={(e) => setRandori({ notes: e.target.value })}
              placeholder="Randori notes (optional)"
              rows={2}
              className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-y"
              data-testid="judo-randori-notes"
            />
          </div>

          {/* Reflection */}
          <div className="p-4 bg-enf-surface-mid border border-enf-surface-border rounded-md space-y-3">
            <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold">Post-session reflection</div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1 block">Did you hit your intent?</label>
              <div className="flex gap-1.5">
                {['Yes', 'Partial', 'No'].map((opt) => {
                  const active = form.reflection.intentHit === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setReflection({ intentHit: active ? '' : opt })}
                      aria-pressed={active}
                      data-testid={`judo-reflection-intent-${opt.toLowerCase()}`}
                      className={`flex-1 px-3 py-1.5 text-xs rounded-md border font-semibold transition-colors ${
                        active
                          ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary'
                          : 'bg-enf-surface-deep border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary'
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
            <textarea
              value={form.reflection.win}
              onChange={(e) => setReflection({ win: e.target.value })}
              placeholder="Biggest win tonight"
              rows={2}
              className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-y"
              data-testid="judo-reflection-win"
            />
            <textarea
              value={form.reflection.gap}
              onChange={(e) => setReflection({ gap: e.target.value })}
              placeholder="Biggest gap to work on"
              rows={2}
              className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-y"
              data-testid="judo-reflection-gap"
            />
            <textarea
              value={form.reflection.nextFocus}
              onChange={(e) => setReflection({ nextFocus: e.target.value })}
              placeholder="Next focus / what to drill next time"
              rows={2}
              className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-y"
              data-testid="judo-reflection-next"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1 block">Free-form notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              placeholder="Anything else from this session"
              rows={3}
              className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-y"
              data-testid="judo-session-notes"
            />
          </div>

          {/* Attachments */}
          <div>
            <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Attachments ({form.attachments.length})</label>
            <div className="space-y-2">
              <MediaCaptureMenu
                onCapture={(payload) => addAttachment(payload)}
                accept="both"
                multiple={false}
                label="Add photo or video"
                testId="judo-attachment-add"
                recordingEtiquette
                className="w-full p-3 border border-dashed border-enf-surface-border hover:border-enf-role-info rounded-md text-sm text-enf-text-secondary hover:text-enf-role-info transition-colors flex items-center justify-center gap-2"
              />
              <PasteUrlInput onAdd={addExternalUrl} testId="judo-paste-url" />
            </div>
            {form.attachments.length > 0 && (
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {form.attachments.map((a) => (
                  <AttachmentTile key={a.id} attachment={a} onRemove={() => removeAttachment(a.id)} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-enf-surface-border flex items-center justify-end gap-2 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm" data-testid="judo-dialog-cancel">
            Cancel
          </button>
          <button onClick={commitSave} className="px-5 py-2 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold" data-testid="judo-dialog-save">
            {isEditMode ? 'Save changes' : 'Save session'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JudoLogDialog;
